import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import type { ActionAlert } from "@/api/action_alerts";
import type { GrantOpportunityFocusRequest } from "@/components/grant_suite/grantSuiteNavigation";
import type { ActiveSection } from "@/types/appNavigation";

type GrantSuiteTab = "discover" | "track";

type GrantSuiteNavigationContextValue = {
  focusRequest: GrantOpportunityFocusRequest | null;
  pendingTab: GrantSuiteTab | null;
  clearGrantOpportunityFocus: () => void;
  clearPendingTab: () => void;
  navigateToGrantOpportunity: (opportunityId: number) => void;
  handleActionAlertSelect: (alert: ActionAlert) => void;
};

const GrantSuiteNavigationContext = createContext<GrantSuiteNavigationContextValue | null>(null);

type GrantSuiteNavigationProviderProps = {
  children: ReactNode;
  setActiveSection: React.Dispatch<React.SetStateAction<ActiveSection>>;
};

export function GrantSuiteNavigationProvider({
  children,
  setActiveSection,
}: GrantSuiteNavigationProviderProps) {
  const [focusRequest, setFocusRequest] = useState<GrantOpportunityFocusRequest | null>(null);
  const [pendingTab, setPendingTab] = useState<GrantSuiteTab | null>(null);

  const clearGrantOpportunityFocus = useCallback(() => {
    setFocusRequest(null);
  }, []);

  const clearPendingTab = useCallback(() => {
    setPendingTab(null);
  }, []);

  const navigateToGrantOpportunity = useCallback(
    (opportunityId: number) => {
      setActiveSection("grant_suite");
      setPendingTab("track");
      setFocusRequest((current) => ({
        opportunityId,
        nonce: (current?.nonce ?? 0) + 1,
      }));
    },
    [setActiveSection],
  );

  const handleActionAlertSelect = useCallback(
    (alert: ActionAlert) => {
      const target = alert.target;
      if (alert.source === "grant_suite" && target?.type === "grant_opportunity") {
        navigateToGrantOpportunity(target.id);
        return;
      }
      if (alert.source === "grant_suite" && alert.opportunity_id) {
        navigateToGrantOpportunity(alert.opportunity_id);
      }
    },
    [navigateToGrantOpportunity],
  );

  const value = useMemo(
    () => ({
      focusRequest,
      pendingTab,
      clearGrantOpportunityFocus,
      clearPendingTab,
      navigateToGrantOpportunity,
      handleActionAlertSelect,
    }),
    [
      focusRequest,
      pendingTab,
      clearGrantOpportunityFocus,
      clearPendingTab,
      navigateToGrantOpportunity,
      handleActionAlertSelect,
    ],
  );

  return (
    <GrantSuiteNavigationContext.Provider value={value}>
      {children}
    </GrantSuiteNavigationContext.Provider>
  );
}

export function useGrantSuiteNavigation() {
  const context = useContext(GrantSuiteNavigationContext);
  if (!context) {
    throw new Error("useGrantSuiteNavigation must be used within GrantSuiteNavigationProvider");
  }
  return context;
}
